import React, { useEffect, useState, Fragment } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../Dashboard/Sidebar';
import Loader from '../layouts/Loader';
import MetaData from '../layouts/MetaData';
import DeleteConfirmModal from '../layouts/DeleteConfirmModal';
import formatDate from '../../utils/formatDate';

export default function TrashBin() {
    const [loading, setLoading] = useState(true);
    const [deleteModal, setDeleteModal] = useState({ show: false, type: '', id: null, name: '' });
    const [trash, setTrash] = useState({
        patients: [],
        bills: [],
        treatments: [],
        staff: [],
        accounts: [],
        diets: []
    });

    const fetchTrash = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get('/api/v1/admin/trash');
            setTrash(data.trash);
            setLoading(false);
        } catch (error) {
            toast(error.response?.data?.message || 'Error fetching trash', { type: 'error' });
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrash();
    }, []);

    const handleRestore = async (type, id) => {
        try {
            await axios.put('/api/v1/admin/trash/restore', { type, id });
            toast('Item restored', { type: 'success' });
            fetchTrash();
        } catch (error) {
            toast(error.response?.data?.message || 'Error restoring item', { type: 'error' });
        }
    };

    const openPermanentDeleteModal = (type, id, name) => {
        setDeleteModal({ show: true, type, id, name: name || 'this item' });
    };

    const confirmPermanentDelete = async () => {
        const { type, id } = deleteModal;
        if (!type || !id) return;
        try {
            await axios.delete('/api/v1/admin/trash/permanent', { data: { type, id } });
            toast('Item permanently deleted', { type: 'success' });
            setDeleteModal({ show: false, type: '', id: null, name: '' });
            fetchTrash();
        } catch (error) {
            toast(error.response?.data?.message || 'Error deleting item', { type: 'error' });
            setDeleteModal({ show: false, type: '', id: null, name: '' });
        }
    };

    const renderTable = (items, type, columns, rowRenderer) => {
        if (!items || items.length === 0) return <p className="text-muted">No deleted {type}s.</p>;

        return (
            <div className="table-responsive mb-4">
                <h5 className="text-capitalize mb-2">{type}s</h5>
                <table className="table table-sm table-bordered table-hover">
                    <thead className="table-light">
                        <tr>
                            {columns.map(col => <th key={col}>{col}</th>)}
                            <th>Deleted At</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(item => (
                            <tr key={item._id}>
                                {rowRenderer(item)}
                                <td>{formatDate(item.deletedAt)}</td>
                                <td className="text-end">
                                    <button 
                                        className="btn btn-sm btn-outline-success me-2"
                                        onClick={() => handleRestore(type, item._id)}
                                    >
                                        <i className="fa fa-refresh me-1"></i>Restore
                                    </button>
                                    <button 
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => openPermanentDeleteModal(type, item._id, item.name || item.title || item.invoiceNumber || item.category)}
                                    >
                                        <i className="fa fa-trash me-1"></i>Delete Forever
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">
                <MetaData title="Trash Bin" />
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <div>
                        <h1 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>
                            <i className="fa fa-trash me-2"></i>Trash Bin
                        </h1>
                        <p className="mb-0 mt-1" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            Items are kept here for 15 days before automatic permanent deletion.
                        </p>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body p-4">
                        {loading ? (
                            <Loader />
                        ) : (
                            <Fragment>
                                {Object.values(trash).every(arr => arr.length === 0) ? (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fa fa-trash-o fa-3x mb-3" style={{ opacity: 0.3 }}></i>
                                        <h5>Trash is empty</h5>
                                    </div>
                                ) : (
                                    <Fragment>
                                        {renderTable(trash.patients, 'patient', ['ID', 'Name'], item => (
                                            <Fragment>
                                                <td>{item.patientId}</td>
                                                <td>{item.name}</td>
                                            </Fragment>
                                        ))}

                                        {renderTable(trash.bills, 'bill', ['Invoice #', 'Total'], item => (
                                            <Fragment>
                                                <td>{item.invoiceNumber}</td>
                                                <td>₹{item.totalAmount}</td>
                                            </Fragment>
                                        ))}

                                        {renderTable(trash.treatments, 'treatment', ['Name', 'Price'], item => (
                                            <Fragment>
                                                <td>{item.name}</td>
                                                <td>₹{item.price}</td>
                                            </Fragment>
                                        ))}

                                        {renderTable(trash.staff, 'staff', ['ID', 'Name'], item => (
                                            <Fragment>
                                                <td>{item.staffId}</td>
                                                <td>{item.name}</td>
                                            </Fragment>
                                        ))}

                                        {renderTable(trash.accounts, 'account', ['Type', 'Category', 'Amount'], item => (
                                            <Fragment>
                                                <td><span className={`badge ${item.type === 'income' ? 'bg-success' : 'bg-danger'}`}>{item.type}</span></td>
                                                <td>{item.category}</td>
                                                <td>₹{item.amount}</td>
                                            </Fragment>
                                        ))}

                                        {renderTable(trash.diets, 'diet', ['Patient', 'Diet Title / Condition', 'Doctor'], item => (
                                            <Fragment>
                                                <td>{item.patient?.name ? `${item.patient.name} (${item.patient.patientId || ''})` : `Patient ID: ${item.patientId}`}</td>
                                                <td><strong>{item.title}</strong> {item.condition ? `(${item.condition})` : ''}</td>
                                                <td>{item.doctor || '-'}</td>
                                            </Fragment>
                                        ))}
                                    </Fragment>
                                )}
                            </Fragment>
                        )}
                    </div>
                </div>

                <DeleteConfirmModal 
                    show={deleteModal.show}
                    title="Permanently Delete Item"
                    itemName={deleteModal.name}
                    actionType="permanently delete"
                    onConfirm={confirmPermanentDelete}
                    onClose={() => setDeleteModal({ show: false, type: '', id: null, name: '' })}
                />
        </div>
        </div>
    );
}




